/**
 * Telas de pagamento do Global (inglês) e do LATAM (espanhol) com textos
 * editáveis por bot: os campos de cada tela e uma prévia no estilo do
 * Telegram, que acompanha o que o seller digita. Campo vazio = texto padrão.
 * Só visual (o MessagesEditor junta as telas em abas).
 */

export const MAX_TEXT_LENGTH = 200;

// Exemplo de pedido nas prévias.
const SAMPLE = {
    usd: { plan: 'VIP', duration: 'Monthly', amount: '$10.00' },
    mxn: { plan: 'VIP', duration: 'Mensual', amount: '$499.00 MXN' },
};

// Rótulos aparecem em mais de uma tela: o mesmo campo vale para todas.
const SHARED = new Set(['label_plan', 'label_duration', 'label_price', 'label_amount', 'label_total']);

/**
 * Telas de cada mercado. `fields`: [chave, nome do campo]; `preview(t, s)`:
 * linhas da mensagem e botões com os textos efetivos (t) e o exemplo (s).
 */
export const SCREENS = {
    usd: {
        // Modelo único (como o SPEI): MethodEditor.SCREEN_TEMPLATES.
        method: { title: 'Escolha da forma de pagamento' },
        // Modelo único (como o SPEI): MethodEditor.SCREEN_TEMPLATES.
        currency: { title: 'Escolha da moeda' },
        // Modelo único (como o SPEI): MethodEditor.SCREEN_TEMPLATES.
        secure: { title: 'Link de pagamento' },
        loading: {
            title: 'Gerando o pagamento',
            when: 'Aparece por alguns segundos, enquanto o pagamento é criado.',
            fields: [['loading', 'Mensagem']],
            preview: (t) => ({ lines: [t.loading], buttons: [] }),
        },
    },
    mxn: {
        // Modelo único (como o SPEI): MethodEditor.SCREEN_TEMPLATES.
        currency: { title: 'Escolha da moeda' },
        // Modelo único (como o SPEI): MethodEditor.SCREEN_TEMPLATES.
        secure: { title: 'Link de pagamento' },
        loading: {
            title: 'Gerando o pagamento',
            when: 'Aparece por alguns segundos, enquanto o pagamento é criado.',
            fields: [['loading', 'Mensagem']],
            preview: (t) => ({ lines: [t.loading], buttons: [] }),
        },
    },
};

/** Prévia simples de uma mensagem do Telegram com botões. */
export function TelegramPreview({ lines, buttons = [], fixedButtons = false, html = null }) {
    return (
        <div data-testid="preview" className="rounded-xl bg-[#17212b] border border-white/[0.06] p-2.5 text-[12px] leading-snug text-white/90">
            <div className="rounded-lg bg-[#182533] px-3 py-2 whitespace-pre-wrap break-words">
                {html !== null
                    ? <span dangerouslySetInnerHTML={{ __html: html }} />
                    : lines.join('\n')}
            </div>
            {buttons.length > 0 && (
                <div className="mt-1 flex flex-col gap-1">
                    {buttons.map((b, i) => (
                        <div key={i} className={`rounded-md bg-white/[0.08] px-2 py-1.5 text-center text-[11px] font-medium ${fixedButtons ? 'text-white/50' : 'text-sky-300'}`}>
                            {b}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Campos de texto de uma lista de chaves (com o padrão como exemplo). */
export function TextFields({ fields, value, defaults, onChange, group }) {
    return (
        <div className="grid gap-3">
            {fields.map(([key, label]) => (
                <label key={key} className="flex flex-col gap-1">
                    <span className="text-xs text-white/50">
                        {label}
                        {SHARED.has(key) && <span className="text-white/30"> · vale em todas as telas</span>}
                    </span>
                    <input
                        type="text"
                        className="input3"
                        aria-label={`${group}: ${label}`}
                        maxLength={MAX_TEXT_LENGTH}
                        placeholder={defaults[key] || ''}
                        value={value[key] || ''}
                        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
                    />
                </label>
            ))}
        </div>
    );
}

/** Uma tela: o que ela é, campos à esquerda e prévia à direita (empilha no celular). */
export function TextScreen({ market, screen, value = {}, defaults = {}, onChange }) {
    const spec = SCREENS[market][screen];
    const effective = { ...defaults, ...Object.fromEntries(Object.entries(value).filter(([, v]) => v && v.trim())) };
    const preview = spec.preview(effective, SAMPLE[market]);
    return (
        <div>
            <p className="spt-hint mb-3">{spec.when}</p>
            <div data-testid="message-layout" className="grid gap-4 md:grid-cols-2 items-start">
                <TextFields fields={spec.fields} value={value} defaults={defaults} onChange={onChange} group={spec.title} />
                <div>
                    <p className="text-[11px] text-white/40 mb-1.5">Prévia</p>
                    <TelegramPreview {...preview} />
                </div>
            </div>
        </div>
    );
}
